import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, Input, Spinner } from '@heroui/react';

// All US states
const ALL_US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const StateListView = () => {
  const [states, setStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStateCounts = async () => {
    try {
      setLoading(true);
      setError(null);

      const API_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8080';
      const response = await fetch(`${API_URL}/api/videos/count-by-state`);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // Handle null or empty states array
      const statesWithVideos = data.states || [];

      // Create a map of state counts
      const stateCountMap = {};
      statesWithVideos.forEach(({ state, count }) => {
        stateCountMap[state] = count;
      });

      // Create array with all states, showing 0 for states without videos
      const allStatesWithCounts = ALL_US_STATES.map(state => ({
        state,
        count: stateCountMap[state] || 0
      }));

      setStates(allStatesWithCounts);
      setFilteredStates(allStatesWithCounts);
    } catch (err) {

      // Graceful fallback: Show all states with 0 videos
      const fallbackStates = ALL_US_STATES.map(state => ({
        state,
        count: 0
      }));
      setStates(fallbackStates);
      setFilteredStates(fallbackStates);

      // Set user-friendly error message
      if (err.message.includes('Failed to fetch') || err.message === 'Load failed' || err.name === 'TypeError') {
        setError('network');
      } else {
        setError('server');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStateCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredStates(states);
    } else {
      const filtered = states.filter(state =>
        state.state.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStates(filtered);
    }
  }, [searchTerm, states]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  const renderErrorBanner = () => {
    if (!error) return null;

    const errorMessages = {
      network: {
        title: "Can't connect to server",
        description: "We're having trouble connecting to the video database. You can still browse all states, but video counts may not be up to date.",
        icon: (
          <svg className="w-12 h-12 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
          </svg>
        )
      },
      server: {
        title: "Server error",
        description: "The server is having issues. You can still browse all states, but video counts may not be accurate.",
        icon: (
          <svg className="w-12 h-12 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      }
    };

    const { title, description, icon } = errorMessages[error] || errorMessages.server;

    return (
      <div className="mb-6">
        <Card className={`border-2 ${error === 'network' ? 'border-warning bg-warning-50' : 'border-danger bg-danger-50'}`}>
          <CardBody>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-shrink-0">
                {icon}
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="font-semibold text-lg mb-1">{title}</h3>
                <p className="text-sm text-foreground-700">{description}</p>
              </div>
              <button
                onClick={fetchStateCounts}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-80 transition-opacity flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry
              </button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-6">
      {/* Error Banner */}
      {renderErrorBanner()}

      {/* Search Bar */}
      <div className="mb-6">
        <Input
          type="text"
          placeholder="Search states..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="bordered"
          size="lg"
          classNames={{
            input: "text-base",
            inputWrapper: "border-2"
          }}
          startContent={
            <svg className="w-5 h-5 text-default-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          }
        />
      </div>

      {/* State Count */}
      <div className="mb-4 text-sm text-foreground-600">
        {filteredStates.length === states.length
          ? `Showing all ${states.length} states`
          : `${filteredStates.length} of ${states.length} states`
        }
      </div>

      {/* States Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 auto-rows-fr">
        {filteredStates.map(({ state, count }) => (
          <Link
            key={state}
            to={`/state/${encodeURIComponent(state)}`}
            className="block w-full h-full"
          >
            <Card
              isPressable={count > 0}
              isHoverable={count > 0}
              className={`border-2 transition-all duration-200 h-full w-full ${
                count > 0
                  ? 'border-default-300 hover:border-primary bg-content1'
                  : 'border-default-200 bg-default-50 opacity-60'
              }`}
            >
              <CardBody className="p-4 h-full w-full">
                <div className="flex items-center justify-between h-full w-full">
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${
                      count > 0 ? 'text-foreground' : 'text-foreground-500'
                    }`}>
                      {state}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      count > 0 ? 'text-foreground-600' : 'text-foreground-400'
                    }`}>
                      {count === 0 ? 'No videos yet' : `${count} ${count === 1 ? 'video' : 'videos'}`}
                    </p>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2">
                    {count > 0 && (
                      <svg
                        className="w-5 h-5 text-foreground-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Link>
        ))}
      </div>

      {/* No Results */}
      {filteredStates.length === 0 && (
        <div className="text-center py-12">
          <p className="text-foreground-600 text-lg">No states found matching "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default StateListView;
